import { TableResponse } from "..";
import { BookingType, EventStatus, FacilityAddOnType } from "../enums";
import { TaxGroup } from "../system-setting/tax-group";

export interface TimeSlot {
  start_time: string;
  end_time: string;
}
export interface DayConfig {
  is_available: boolean;
  time_slots: TimeSlot;
}
export type DaysKey = "S" | "M" | "T" | "W" | "TH" | "F" | "SA";
export type DaysState = Record<DaysKey, DayConfig>;
// Weekly Availability Interface
interface WeeklyAvailability {
  monday: DayConfig;
  tuesday: DayConfig;
  wednesday: DayConfig;
  thursday: DayConfig;
  friday: DayConfig;
  saturday: DayConfig;
  sunday: DayConfig;
}

export enum ScheduleType {
  FacilitySchedule = "facility_schedule",
  Events = "events",
}

export type ScheduleEditScope = "this" | "this_and_following" | "all";

export interface FacilityGeneralInfo {
  id?: number;
  name?: string;
  status?: EventStatus;
  trainer_name?: string;
  type: ScheduleType;
  facility_id: number;
  remaining_capacity?: number;
  facility_name?: string;
  break_duration?: number;
  income_category_id?: number;
  income_category_name?: string;
  tax_group_id?: number;
  tax_group?: TaxGroup;
  sale_tax_id?: number;
  sale_tax_name?: string;
  tax_perc?: number;
  color: string;
  price?: number;
  base_price?: number;
  duration_per_slot: number;
  max_attendees?: number;
  is_exclusive: boolean;
  member_only: boolean;
  location?: string;
  description?: string;
  advance_booking_days?: number;
  always_available: boolean;
  start_date: string; // ISO date string format (YYYY-MM-DD)
  end_date?: string; // ISO date string format (YYYY-MM-DD)
  price_master_id?: number;
  start_time?: string;
  end_time?: string;
  availability?: WeeklyAvailability; // Made optional as backend might not require it for all flows
  rrule_str?: string;
  recurrence?: import("@/components/admin/facility_schedule/modal/recurrence-selector").RecurrenceConfig;

  is_srb?: boolean;
  revenue_type?: string;
  sales_tax_id?: number;
  sales_tax_name?: string;
  sales_tax_perc?: number; // Added sales_tax_perc
}

//Tier Pricing Interface
interface TierPricing {
  quantity: number;
  price: number;
}

// Membership Plan Pricing Interface
interface MembershipPlan {
  group_id: number;
  membership_ids: number[];
  price: number;
}

// Group Pricing Interface
interface GroupPricing {
  group_id: number;
  price: number;
}

// Special Pricing Interface
interface SpecialPricing {
  name: string;
  price: number;
  date_from: string; // ISO date string format (YYYY-MM-DD)
  date_to: string; // ISO date string format (YYYY-MM-DD)
}

// Refund Policy Interface
interface RefundPolicy {
  cancellation_period: string;
  refund_percentage: number;
}

export interface FacilityFormData {
  // General Info
  general_info: FacilityGeneralInfo;
  duration_per_slot: number;
  // Availability
  days: {
    [key: string]: {
      is_available: boolean;
      time_slots: {
        start_time: string;
        end_time: string;
      };
    };
  };

  // Slots & Schedule - you may need to adjust based on actual needs
  slots?: any[];

  // Pricing & Refund
  regularPricing: number;
  tierPricingEnabled: boolean;
  tier_pricing: Array<{
    quantity: number;
    price: number;
  }>;
  planBasedPricing: boolean;
  plans: Array<{
    group_id: number;
    membership_ids: number[];
    price: number;
  }>;
  group_pricing: Array<{
    group_id: number;
    price: number;
  }>;
  specialPricing: boolean;
  special_pricing: Array<{
    name: string;
    price: number;
    date_from?: string;
    date_to?: string;
  }>;
  refund_policies: Array<{
    period: string;
    refundPercent: number;
  }>;
}
export interface FacilityScheduleAddOn {
  id: number;
  max_attendees: number;
  price: number;
  name: string;
  type: FacilityAddOnType;
  item_id: number;
  item_type: "facility" | "product" | "trainer" | "consultation";
  facility_type?: string;
  description?: string;
  access_control?: boolean;
  price_master_id?: number;
}

export interface FacilitySlotAddOn {
  id: number;
  max_attendees: number;
  quantity?: number;
  min_limit?: number;
  price: number;
  name: string;
  type: FacilityAddOnType;
  item_id: number;
  item_type: "facility" | "product" | "trainer" | "consultation";
  facility_type?: string;
  description?: string;
  access_control?: boolean;
  is_available?: boolean; // For slot-level availability override
  available_quantity?: number; // For tracking remaining quantity if max_attendees is set
  price_master_id?: number;
}


export interface FacilitySchedule {
  org_id: number;
  add_ons?: FacilityScheduleAddOn[];
  general_info: FacilityGeneralInfo;
  tier_pricing: TierPricing[];
  plan_pricing?: MembershipPlan[]; // Optional since it's not always in payload
  group_pricing?: GroupPricing[]; // Optional group-based pricing
  special_pricing: SpecialPricing[];
  refund_policies: RefundPolicy[];
  slots?: FacilityScheduleSlot[];
  add_on_max_attendees?: { [addOnId: number]: number }; // Map of add-on ID to max attendees
  slot_overlaps?: never; // Validation field for slot overlaps (never sent to API)
  status?: string; // Event/Schedule status (upcoming, ongoing, completed)
  event_attendees?: any[]; // Attendees for this schedule
}

export type FacilityScheduleTable = TableResponse<FacilitySchedule>;
export interface ConsumedSlot {
  slot_id: number;
  booking_id: number;
  member_name: string;
  member_email: string;
  profile_img: string;
  start_time: string;
  end_time: string;
  booking_date: string;
  created_at: string;
}
export interface FacilityScheduleSlot {
  id: number;
  name: string;
  type: ScheduleType;
  facility_schedule_id?: number;
  availability?: WeeklyAvailability;
  facility_id: number;
  facility_name: string;
  start_date?: string;
  end_date?: string;
  description?: string;
  slot_date: string;
  day_name: string;
  start_time: string;
  end_time: string;
  duration_hours: number;
  always_available?: boolean;
  color?: string;
  location?: string;
  add_on_ids?: number[];
  price?: number;
  base_price?: number;
  member_only?: boolean;
  is_exclusive?: boolean;
  add_ons?: FacilitySlotAddOn[];
  consumed_slots?: ConsumedSlot[];
  remaining_capacity?: number;
}

export type FacilityScheduleSlots = TableResponse<FacilityScheduleSlot>;

export interface SlotDetailResponse {
  facility_schedule_id: number;
  facility_id: number;
  slot_date: string;
  start_time: string;
  end_time: string;
  duration_per_slot?: number;
  price: number;
  base_price?: number;
  price_master_id?: number;
  tax_group_id?: number;
  income_category_id?: number;
  color?: string;
  location?: string;
  max_attendees?: number;
  advance_booking_days?: number;
  member_only?: boolean;
  name?: string;
  facility_name?: string;
  description?: string;
  type?: string;
  addons: Array<{
    id: number;
    name: string;
    price: number;
    base_price?: number;
    type: string;
    item_type: string;
    item_id?: number;
    quantity?: number;
    min_limit?: number;
    max_attendees?: number;
    facility_type?: string;
    description?: string;
    access_control?: boolean;
    is_available?: boolean;
    available_quantity?: number;
    price_master_id?: number;
  }>;
  refund_policy: any[];
}
